import { useState } from 'react';
import { commissionModels } from '../data';

const transparencyLabels: Record<string, { level: string; pct: number; color: string }> = {
  'Raw ECN Commission': { level: 'Fully transparent', pct: 95, color: 'bg-accent-500' },
  'Spread-Only (Commission-Free)': { level: 'Fully hidden', pct: 10, color: 'bg-foreground-400' },
  'Hybrid (Spread + Reduced Commission)': { level: 'Partially visible', pct: 50, color: 'bg-secondary-500' },
};

export default function CommissionModelsSection() {
  const [activeModel, setActiveModel] = useState<string | null>(null);

  const ecnModel = commissionModels[0];
  const otherModels = commissionModels.slice(1);

  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[36px] md:text-[48px] font-[750] leading-[1.08] text-foreground-950 tracking-tight mb-4">
            Three ways brokers<br className="hidden sm:block" /> charge you per trade
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600 max-w-[560px]">
            Same trade, same lot size — but the model you choose can double or halve your cost. Here is how each one actually works.
          </p>
        </div>

        {/* === FEATURED: Raw ECN (the one that matters most) === */}
        <div className="mb-10">
          <ModelHeroCard
            model={ecnModel}
            isExpanded={activeModel === ecnModel.type}
            onToggle={() => setActiveModel(activeModel === ecnModel.type ? null : ecnModel.type)}
            transparency={transparencyLabels[ecnModel.type]}
            featured
          />
        </div>

        {/* === SECONDARY: The other two === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {otherModels.map((model) => (
            <ModelCompactCard
              key={model.type}
              model={model}
              isExpanded={activeModel === model.type}
              onToggle={() => setActiveModel(activeModel === model.type ? null : model.type)}
              transparency={transparencyLabels[model.type]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Featured Hero Card (ECN) ─── */

function ModelHeroCard({
  model,
  isExpanded,
  onToggle,
  transparency,
  featured,
}: {
  model: typeof commissionModels[number];
  isExpanded: boolean;
  onToggle: () => void;
  transparency: { level: string; pct: number; color: string };
  featured?: boolean;
}) {
  return (
    <div className="group relative bg-foreground-950 rounded-card overflow-hidden transition-all duration-500">
      {/* Subtle decorative gradient in corner */}
      <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-accent-500/8 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-accent-500/5 rounded-full blur-[80px] pointer-events-none" />

      {/* Diagonal "Recommended" ribbon — top-right corner */}
      <div className="absolute -top-1 -right-1 w-[150px] h-[150px] overflow-hidden pointer-events-none z-10">
        <div className="absolute top-[30px] -right-[45px] w-[220px] py-1.5 bg-accent-500 text-center rotate-45 shadow-sm">
          <span className="text-[11px] font-extrabold text-background-50 uppercase tracking-[0.22em] whitespace-nowrap">
            Recommended
          </span>
        </div>
      </div>

      <div className="relative p-6 md:p-10 flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* ── LEFT: Descriptive content ── */}
        <div className="flex-1 min-w-0">
          {/* Top row: broker label */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[13px] text-foreground-300 font-medium">{model.broker}</span>
          </div>

          {/* Model name */}
          <h3 className="text-[28px] md:text-[36px] font-[750] leading-[1.1] text-background-50 mb-4">
            {model.type}
          </h3>

          {/* Description */}
          <p className="text-[15px] leading-relaxed text-foreground-300 max-w-[520px] mb-6">
            {model.desc}
          </p>

          {/* Pros as tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {model.pros.map((pro) => (
              <span
                key={pro}
                className="px-3 py-1.5 rounded-full bg-background-50/10 text-background-50/80 text-[13px] font-medium border border-background-50/10"
              >
                {pro}
              </span>
            ))}
          </div>

          {/* Cons — only show on expand */}
          <button
            onClick={onToggle}
            className="flex items-center gap-2 text-[13px] font-semibold text-foreground-400 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>{isExpanded ? 'Hide trade-offs' : 'See trade-offs'}</span>
            <i className={`w-4 h-4 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
          </button>

          {isExpanded && (
            <div className="mt-5 pt-5 border-t border-background-50/10">
              <div className="flex flex-wrap gap-2 mb-5">
                {model.cons.map((con) => (
                  <span
                    key={con}
                    className="px-3 py-1.5 rounded-full bg-foreground-800 text-foreground-300 text-[13px] font-medium"
                  >
                    {con}
                  </span>
                ))}
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50/5 border border-background-50/10">
                <i className="ri-user-star-line w-5 h-5 flex items-center justify-center text-accent-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">Best For</span>
                  <p className="text-[14px] font-semibold text-background-50 mt-1 leading-snug">{model.bestFor}</p>
                </div>
              </div>
            </div>
          )}

          {/* Best for (always visible, compact) */}
          {!isExpanded && (
            <div className="flex items-center gap-2 pt-5 border-t border-background-50/10">
              <i className="ri-user-star-line w-4 h-4 flex items-center justify-center text-accent-400 flex-shrink-0" />
              <span className="text-[13px] text-foreground-400 leading-snug">{model.bestFor}</span>
            </div>
          )}
        </div>

        {/* ── RIGHT: Rate dashboard — the visual anchor ── */}
        <div className="lg:w-[380px] lg:flex-shrink-0">
          <div className="bg-foreground-900/60 rounded-card p-6 md:p-8 h-full flex flex-col justify-center">
            {/* Rate structure label */}
            <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3 block">
              Round-Turn Rate
            </span>

            {/* BIG rate number */}
            <div className="mb-1">
              <span className="text-[64px] md:text-[80px] font-[800] leading-none text-background-50 tracking-tight">
                {model.rate.split(' ')[0]}
              </span>
            </div>

            {/* Unit line */}
            <p className="text-[15px] text-foreground-300 font-medium mb-6">
              {model.rate.split(' ').slice(1).join(' ')}
            </p>

            {/* Structure description */}
            <p className="text-[13px] text-foreground-400 mb-8">{model.structure}</p>

            {/* Cost transparency meter */}
            <div className="mt-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">Transparency</span>
                <span className="text-[12px] font-bold text-background-50">{transparency.level}</span>
              </div>
              <div className="h-2.5 bg-foreground-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${transparency.color}`}
                  style={{ width: `${transparency.pct}%` }}
                />
              </div>
            </div>

            {/* Decorative dots row */}
            <div className="flex items-center gap-3 mt-6 pt-5 border-t border-foreground-800">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500/60" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500/40" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500/20" />
              </div>
              <span className="text-[10px] text-foreground-500 uppercase tracking-widest font-semibold">Lowest Cost</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Compact Secondary Cards ─── */

function ModelCompactCard({
  model,
  isExpanded,
  onToggle,
  transparency,
}: {
  model: typeof commissionModels[number];
  isExpanded: boolean;
  onToggle: () => void;
  transparency: { level: string; pct: number; color: string };
}) {
  return (
    <div className="group relative bg-background-50 rounded-card border border-background-200 overflow-hidden transition-all duration-400 hover:-translate-y-1 hover:border-background-300">
      <div className="p-6 md:p-7">
        {/* Top label */}
        <span className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wider mb-2 block">
          {model.broker}
        </span>

        {/* Model name */}
        <h3 className="text-[20px] md:text-[22px] font-[750] leading-[1.2] text-foreground-950 mb-5">
          {model.type}
        </h3>

        {/* Rate — prominent but not as massive as hero */}
        <div className="mb-5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[42px] md:text-[48px] font-[800] leading-none text-foreground-950 tracking-tight">
              {model.rate.split(' ')[0]}
            </span>
            <span className="text-[13px] text-foreground-500 font-medium leading-tight">
              {model.rate.split(' ').slice(1).join(' ')}
            </span>
          </div>
          <p className="text-[12px] text-foreground-500 mt-1.5">{model.structure}</p>
        </div>

        {/* Transparency meter — compact */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Transparency</span>
            <span className="text-[11px] font-semibold text-foreground-700">{transparency.level}</span>
          </div>
          <div className="h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${transparency.color}`}
              style={{ width: `${transparency.pct}%` }}
            />
          </div>
        </div>

        {/* Description */}
        <p className="text-[14px] leading-relaxed text-foreground-600 mb-5">
          {model.desc}
        </p>

        {/* Pros — show ALL as tags, no "+N more" nonsense */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {model.pros.map((pro) => (
            <span
              key={pro}
              className="px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 text-[12px] font-medium"
            >
              {pro}
            </span>
          ))}
        </div>

        {/* Expand toggle — same logic as hero card */}
        <button
          onClick={onToggle}
          className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span>{isExpanded ? 'Hide trade-offs' : 'See trade-offs'}</span>
          <i className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
        </button>

        {/* Expanded: Cons + Best For — no duplicate pros */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-background-200 space-y-4">
            {/* Cons */}
            <div>
              <div className="flex flex-wrap gap-1.5">
                {model.cons.map((con) => (
                  <span key={con} className="px-2.5 py-1 rounded-full bg-foreground-100 text-foreground-600 text-[12px] font-medium">
                    {con}
                  </span>
                ))}
              </div>
            </div>

            {/* Best for */}
            <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-accent-50 border border-accent-200">
              <i className="ri-user-star-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] font-semibold text-accent-600 uppercase tracking-wider">Best For</span>
                <p className="text-[13px] font-semibold text-accent-800 mt-0.5 leading-snug">{model.bestFor}</p>
              </div>
            </div>
          </div>
        )}

        {/* Compact best-for when collapsed — same as hero card */}
        {!isExpanded && (
          <div className="flex items-center gap-2 pt-4 border-t border-background-200">
            <i className="ri-user-star-line w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0" />
            <span className="text-[12px] text-foreground-500 leading-snug">{model.bestFor}</span>
          </div>
        )}
      </div>
    </div>
  );
}